// Import statements at the top of your file
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

const GameOverModal = ({ handleRefreshPage, onClose, days = 0, money = 0, userId }) => {
  const [showShareSuccess, setShowShareSuccess] = useState(false);
  const [showStatAnimation, setShowStatAnimation] = useState(false);
  const [animatedMoney, setAnimatedMoney] = useState(0);
  const [animatedDays, setAnimatedDays] = useState(0);
  const [activeTab, setActiveTab] = useState('results'); // 'results' or 'feedback'
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [hideModalInFuture, setHideModalInFuture] = useState(false);

  // Feedback form state
  const [rating, setRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackCategory, setFeedbackCategory] = useState('general');
  const [submitLoading, setSubmitLoading] = useState(false);

  // Format currency function
  const formatMoney = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  useEffect(() => {
    document.body.classList.add('overflow-hidden');

    // Re-enable scroll when modal is closed (component unmounts)
    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  }, []);

  // Initialize animations
  useEffect(() => {
    // Add slight delay for better UX
    setTimeout(() => {
      setShowStatAnimation(true);
    }, 400);
  }, []);

  // Animate the numbers counting up
  useEffect(() => {
    if (showStatAnimation) {
      // Animate days counting up
      const dayDuration = 1500; // 1.5 seconds
      const dayIncrement = days / (dayDuration / 20); // Update every 20ms
      let currentDays = 0;
      const daysInterval = setInterval(() => {
        currentDays = Math.min(currentDays + dayIncrement, days);
        setAnimatedDays(Math.floor(currentDays));
        if (currentDays >= days) {
          clearInterval(daysInterval);
        }
      }, 20);

      // Animate money counting down or up depending on sign
      const moneyDuration = 2000; // 2 seconds
      const moneyIncrement = Math.abs(money) / (moneyDuration / 20); // Update every 20ms
      let currentMoney = 0;
      const moneyInterval = setInterval(() => {
        currentMoney = Math.min(currentMoney + moneyIncrement, Math.abs(money));
        // Apply the original sign back to the animated value
        setAnimatedMoney(money >= 0 ? Math.floor(currentMoney) : -Math.floor(currentMoney));
        if (currentMoney >= Math.abs(money)) {
          clearInterval(moneyInterval);
          // Ensure the final value is exact
          setAnimatedMoney(money);
        }
      }, 20);

      return () => {
        clearInterval(daysInterval);
        clearInterval(moneyInterval);
      };
    }
  }, [showStatAnimation, days, money]);

  // Create a shareable message with game stats and hashtags
  const createShareMessage = () => {
    return `I survived ${days} days in HotelHero before going bankrupt with ${formatMoney(money)}! Can you do better? Play now at https://www.hotelhero.fun #HotelHero #GameOver #HotelSimulator`;
  };

  // Share to Twitter
  const shareToTwitter = () => {
    const message = createShareMessage();
    const url = encodeURIComponent(window.location.href);
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}&url=${url}`, '_blank');
  };

  // Share to Facebook
  const shareToFacebook = () => {
    const shareText = createShareMessage();
    const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}&quote=${encodeURIComponent(shareText)}`;
    try {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    } catch (error) {
      console.error('Error sharing to Facebook:', error);
      // Fallback if popup blocker prevents open
      window.location.href = shareUrl;
    }
  };

  // Share to Reddit
  const shareToReddit = () => {
    const message = createShareMessage();
    const url = encodeURIComponent(window.location.href);
    // Use a more concise title, let the message be the main content
    const title = encodeURIComponent(`I survived ${days} days in HotelHero!`);
    window.open(`https://www.reddit.com/submit?url=${url}&title=${title}`, '_blank');
  };

  // Share to WhatsApp
  const shareToWhatsApp = () => {
    const message = createShareMessage();
    // No need to encode URL for WhatsApp text usually
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  // Copy to clipboard
  const copyToClipboard = () => {
    const message = createShareMessage();
    navigator.clipboard.writeText(message).then(() => {
      setShowShareSuccess(true);
      setTimeout(() => setShowShareSuccess(false), 2000);
    }).catch(err => {
      console.error('Failed to copy text: ', err);
      // Maybe show an error message to the user
    });
  };

  // Handle feedback submission
  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (rating === 0) {
      alert("Please select a rating before submitting.");
      return;
    }

    try {
      setSubmitLoading(true);

      // Submit feedback to backend
      const response = await axios.post('/api/feedback', {
        userId: userId || 'anonymous',
        rating,
        category: feedbackCategory,
        message: feedbackText,
        gameStats: {
          days,
          finalMoney: money
        },
        hideInFuture: hideModalInFuture,
        timestamp: new Date().toISOString()
      });

      if (response.status === 200 || response.status === 201) {
        setFeedbackSubmitted(true);

        // If user chose to hide in future, save to localStorage and close modal
        if (hideModalInFuture) {
          localStorage.setItem('hideGameOverModal', 'true');
          if (onClose) onClose();
        }

        // Award 50 coins for feedback submission
        try {
          if (userId) {
            await axios.post('/api/users/award-credits', {
              userId,
              amount: 50,
              reason: 'feedback_submission'
            });
          }
        } catch (creditError) {
          console.error('Error awarding credits:', creditError);
          // Continue even if credit award fails
        }
      } else {
        throw new Error('Failed to submit feedback');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('There was a problem submitting your feedback. Please try again later.');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Handle skipping feedback in future
  const handleSkipFuture = () => {
    localStorage.setItem('hideGameOverModal', 'true');
    if (onClose) onClose();
  };

  // Check if user has previously chosen to hide this modal on component mount
  useEffect(() => {
    const checkSavedPreference = async () => {
      try {
        const token = localStorage.getItem('hideGameOverModal');
        if (token === 'true') {
          // Auto-close immediately if preference is set
          if (onClose) onClose();
        }
      } catch (error) {
        console.error("Error checking saved modal preference:", error);
      }
    };

    checkSavedPreference();
  }, [onClose]);

  return (
    <AnimatePresence>
      {/* Backdrop */}
      <motion.div
        className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {/* Modal Container */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.8, opacity: 0, y: 20 }}
          transition={{ type: 'spring', damping: 20, stiffness: 200 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md overflow-hidden border border-red-700/20 flex flex-col max-h-[90vh]"
        >
          {/* Header */}
          <div className="bg-gradient-to-br from-red-800 via-red-600 to-red-500 p-6 text-center relative overflow-hidden flex-shrink-0">
            {/* Background pattern */}
            <div className="absolute inset-0 opacity-10">
              <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <pattern id="bankruptcyPattern" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                    <path d="M0 20 L40 20" stroke="white" strokeWidth="0.5" />
                    <path d="M20 0 L20 40" stroke="white" strokeWidth="0.5" />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#bankruptcyPattern)" />
              </svg>
            </div>
            {/* Main game over image */}
            <motion.div
              className="mb-2 relative z-10 mx-auto"
              initial={{ y: -50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, type: 'spring', damping: 12 }}
            >
              <img
                src="/gameover.png"
                alt="Game Over"
                width="100"
                height="100"
                className="mx-auto drop-shadow-lg"
                loading="lazy"
              />
            </motion.div>
            {/* Title */}
            <motion.h2
              className="text-4xl font-bold text-white mb-1 drop-shadow-lg relative z-10"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3, type: 'spring', damping: 12 }}
            >
              GAME OVER
            </motion.h2>
            {/* Subtitle */}
            <motion.p
              className="text-red-100 relative z-10 text-lg"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              You've gone bankrupt!
            </motion.p>
          </div>
          {/* Tabs */}
          <div className="flex border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <button
              onClick={() => setActiveTab('results')}
              className={`flex-1 py-3 px-4 text-center transition-colors ${
                activeTab === 'results'
                  ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400 font-medium'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Game Results
            </button>
            <button
              onClick={() => setActiveTab('feedback')}
              className={`flex-1 py-3 px-4 text-center transition-colors ${
                activeTab === 'feedback'
                  ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400 font-medium'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Give Feedback
            </button>
          </div>
          {/* Content Area */}
          <div className="px-6 pt-5 pb-4 overflow-y-auto flex-grow">
            {/* Game Results Tab */}
            {activeTab === 'results' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200">Your Final Stats</h3>
                {/* Stats Cards */}
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {/* Days survived stat card */}
                  <motion.div
                    className="bg-gradient-to-br from-gray-100 to-gray-50 dark:from-gray-700 dark:to-gray-800 p-4 rounded-lg shadow-sm overflow-hidden relative"
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.5 }}
                  >
                    <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Days Survived</div>
                    <div className="text-2xl font-bold text-gray-800 dark:text-gray-200">
                      {animatedDays}
                    </div>
                    <svg className="absolute right-2 bottom-2 w-10 h-10 text-gray-200 dark:text-gray-700 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                    </svg>
                  </motion.div>
                  {/* Final balance stat card */}
                  <motion.div
                    className="bg-gradient-to-br from-gray-100 to-gray-50 dark:from-gray-700 dark:to-gray-800 p-4 rounded-lg shadow-sm overflow-hidden relative"
                    initial={{ x: 20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.5 }}
                  >
                    <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Final Balance</div>
                    <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                      {formatMoney(animatedMoney)}
                    </div>
                    <svg className="absolute right-2 bottom-2 w-10 h-10 text-gray-200 dark:text-gray-700 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                  </motion.div>
                </div>
                {/* Share Section */}
                <motion.div
                  className="mb-6 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-800 p-4 rounded-lg shadow-sm border border-blue-100 dark:border-gray-600"
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.6 }}
                >
                  <h4 className="font-medium text-blue-800 dark:text-blue-300 mb-3 flex items-center">
                    <svg className="w-4 h-4 mr-1.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="18" cy="5" r="3" />
                      <circle cx="6" cy="12" r="3" />
                      <circle cx="18" cy="19" r="3" />
                      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                    </svg>
                    Share Your Results
                  </h4>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {/* Twitter */}
                    <button
                      onClick={shareToTwitter}
                      className="px-3 py-2 bg-[#1DA1F2] text-white rounded-lg text-sm font-medium hover:bg-[#0c85d0] transition-colors flex items-center shadow-sm hover:shadow active:shadow-inner"
                      aria-label="Share to Twitter"
                      type="button"
                    >
                      <svg className="w-4 h-4 mr-1.5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M23.44 4.83c-.8.37-1.5.38-2.22.02.93-.56.98-.96 1.32-2.02-.88.52-1.86.9-2.9 1.1-.82-.88-2-1.43-3.3-1.43-2.5 0-4.55 2.04-4.55 4.54 0 .36.03.7.1 1.04-3.77-.2-7.12-2-9.36-4.75-.4.67-.6 1.45-.6 2.3 0 1.56.8 2.95 2 3.77-.74-.03-1.44-.23-2.05-.57v.06c0 2.2 1.56 4.03 3.64 4.44-.67.2-1.37.2-2.06.08.58 1.8 2.26 3.12 4.25 3.16-1.9 1.47-4.3 2.35-6.9 2.35-.45 0-.9-.03-1.33-.08 2.46 1.58 5.38 2.5 8.5 2.5 10.2 0 15.75-8.42 15.75-15.75 0-.23 0-.47-.02-.7.85-.62 1.6-1.38 2.18-2.26z" />
                      </svg>
                      Twitter
                    </button>
                    {/* Facebook */}
                    <button
                      onClick={shareToFacebook}
                      className="px-3 py-2 bg-[#1877F2] text-white rounded-lg text-sm font-medium hover:bg-[#0c63d4] transition-colors flex items-center shadow-sm hover:shadow active:shadow-inner"
                      aria-label="Share to Facebook"
                      type="button"
                    >
                      <svg className="w-4 h-4 mr-1.5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M9.75 21v-9h-3v-3.75h3v-3c0-2.87 1.75-4.5 4.38-4.5 1.25 0 2.32.12 2.62.12v3.38h-1.75c-1.5 0-1.75.75-1.75 1.75v2.25h3.5l-.5 3.75h-3v9h-3.5z" />
                      </svg>
                      Facebook
                    </button>
                    {/* Reddit */}
                    <button
                      onClick={shareToReddit}
                      className="px-3 py-2 bg-[#FF4500] text-white rounded-lg text-sm font-medium hover:bg-[#e03d00] transition-colors flex items-center shadow-sm hover:shadow active:shadow-inner"
                      aria-label="Share to Reddit"
                      type="button"
                    >
                      <svg className="w-4 h-4 mr-1.5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.01 16.98c-.14.14-.33.21-.53.21-.2 0-.39-.07-.53-.21l-2.79-2.79c-.15-.15-.35-.23-.56-.23s-.41.08-.56.23L9.28 16.98c-.14.14-.33.21-.53.21-.2 0-.39-.07-.53-.21-.29-.3-.29-.78 0-1.07l2.79-2.79c.3-.3.78-.3 1.07 0l2.79 2.79c.3.3.3.78 0 1.07zm-1.01-4.68c-.99 0-1.8-.81-1.8-1.8s.81-1.8 1.8-1.8 1.8.81 1.8 1.8-.81 1.8-1.8 1.8zm-6 0c-.99 0-1.8-.81-1.8-1.8s.81-1.8 1.8-1.8 1.8.81 1.8 1.8-.81 1.8-1.8 1.8zM12 6.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5z"/>
                      </svg>
                      Reddit
                    </button>
                    {/* WhatsApp */}
                    <button
                      onClick={shareToWhatsApp}
                      className="px-3 py-2 bg-[#25D366] text-white rounded-lg text-sm font-medium hover:bg-[#20bd59] transition-colors flex items-center shadow-sm hover:shadow active:shadow-inner"
                      aria-label="Share via WhatsApp"
                      type="button"
                    >
                      <svg className="w-4 h-4 mr-1.5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19.11 4.9C17.17 2.96 14.67 1.9 12 1.9S6.83 2.96 4.9 4.9C2.96 6.83 1.9 9.33 1.9 12s.96 5.17 2.99 7.11C6.83 21.04 9.33 22.1 12 22.1c1.08 0 2.13-.17 3.12-.49l3.83.99-1.01-3.74c.75-1.03 1.29-2.21 1.57-3.49.39-1.61.47-3.27.29-4.9zm-7.11 13.17c-1.76 0-3.52-.48-5.08-1.38l-.36-.21-3.78.98 1.01-3.67-.24-.38c-1-1.58-1.53-3.4-1.53-5.28 0-5.46 4.44-9.91 9.94-9.91 2.65 0 5.14 1.04 7.02 2.91 1.88 1.88 2.91 4.37 2.91 7.02 0 5.46-4.44 9.91-9.94 9.91zM15.95 14.4c-.3-.15-1.77-.88-2.04-.98s-.47-.15-.67.15c-.2.3-.77.98-.94 1.18s-.35.22-.65.08c-.3-.15-1.26-.47-2.4-1.5s-1.48-1.78-1.66-2.08c-.17-.3-.02-.46.13-.61s.3-.35.45-.52c.15-.18.2-.3.3-.5s.05-.38-.02-.53c-.08-.15-.67-1.62-.92-2.2s-.49-.51-.67-.51c-.17 0-.37 0-.57 0s-.52.08-.8.38c-.27.3-1.04 1.02-1.04 2.48s1.07 2.89 1.22 3.09c.15.2 2.1 3.2 5.1 4.5.71.3 1.27.48 1.7.63.71.23 1.36.2 1.88.12.57-.08 1.77-.72 2.02-1.43.25-.71.25-1.3.18-1.43s-.27-.21-.57-.35z"/>
                      </svg>
                      WhatsApp
                    </button>
                    {/* Copy to clipboard */}
                    <button
                      onClick={copyToClipboard}
                      className="px-3 py-2 bg-gray-700 text-white rounded-lg text-sm font-medium hover:bg-gray-600 transition-colors flex items-center shadow-sm hover:shadow active:shadow-inner"
                      aria-label="Copy result to clipboard"
                      type="button"
                    >
                      <svg className="w-4 h-4 mr-1.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                        <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                      </svg>
                      Copy Text
                    </button>
                  </div>
                  {/* Success message */}
                  <AnimatePresence>
                    {showShareSuccess && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="text-green-600 dark:text-green-400 text-sm flex items-center mt-2"
                      >
                        <svg className="w-4 h-4 mr-1.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        Copied to clipboard!
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
                {/* Encouragement Text */}
                <motion.div
                  className="text-center mb-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.7 }}
                >
                  <p className="text-gray-600 dark:text-gray-400">Better luck next time! Ready for another challenge?</p>
                </motion.div>
              </motion.div>
            )}
            {/* Feedback Tab */}
            {activeTab === 'feedback' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="pt-1"
              >
                {!feedbackSubmitted ? (
                  <form onSubmit={handleFeedbackSubmit}>
                    <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200">Your Feedback Matters!</h3>

                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-5">
                      Help us improve HotelHero with your valuable feedback. Let us know what you think about the game!
                    </p>

                    {/* Rating */}
                    <div className="mb-5">
                      <label className="block text-gray-700 dark:text-gray-300 mb-2 font-medium">
                        Rate Your Experience
                      </label>
                      <div className="flex justify-center space-x-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => setRating(star)}
                            className={`w-10 h-10 rounded-full flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${
                              star <= rating
                                ? 'bg-yellow-400 text-white hover:bg-yellow-500'
                                : 'bg-gray-200 text-gray-400 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600'
                            }`}
                            aria-label={`Rate ${star} stars`}
                          >
                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63L2 9.24l5.46 4.73L5.82 21z"/></svg>
                          </button>
                        ))}
                      </div>
                      {rating > 0 && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 text-center">You rated it {rating} star{rating > 1 ? 's' : ''}.</p>
                      )}
                    </div>
                    {/* Feedback Category */}
                    <div className="mb-4">
                      <label htmlFor="feedbackCategory" className="block text-gray-700 dark:text-gray-300 mb-2 font-medium">
                        Category
                      </label>
                      <select
                        id="feedbackCategory"
                        className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                        value={feedbackCategory}
                        onChange={(e) => setFeedbackCategory(e.target.value)}
                      >
                        <option value="general">General Feedback</option>
                        <option value="gameplay">Gameplay</option>
                        <option value="difficulty">Difficulty</option>
                        <option value="bugs">Bugs/Issues</option>
                        <option value="suggestions">Suggestions</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    {/* Feedback Text */}
                    <div className="mb-4">
                      <label htmlFor="feedbackText" className="block text-gray-700 dark:text-gray-300 mb-2 font-medium">
                        Tell us more...
                      </label>
                      <textarea
                        id="feedbackText"
                        rows="4"
                        className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                        placeholder="What did you like or dislike? Any suggestions?"
                        value={feedbackText}
                        onChange={(e) => setFeedbackText(e.target.value)}
                      ></textarea>
                    </div>
                    {/* Hide in Future Checkbox */}
                    <div className="mb-4 flex items-center">
                      <input
                        id="hideModalInFuture"
                        type="checkbox"
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                        checked={hideModalInFuture}
                        onChange={(e) => setHideModalInFuture(e.target.checked)}
                      />
                      <label htmlFor="hideModalInFuture" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                        Don't show this again in the future.
                      </label>
                    </div>
                    {/* Submit Button */}
                    <button
                      type="submit"
                      className={`w-full py-3 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${
                        submitLoading
                          ? 'bg-blue-400 cursor-not-allowed'
                          : 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600'
                      }`}
                      disabled={submitLoading}
                    >
                      {submitLoading ? (
                        <svg className="animate-spin h-5 w-5 mx-auto text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        'Submit Feedback'
                      )}
                    </button>
                  </form>
                ) : (
                  <div className="text-center py-8">
                    <svg className="mx-auto mb-4 w-12 h-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <h4 className="mb-2 text-xl font-semibold text-gray-800 dark:text-gray-200">Thank You!</h4>
                    <p className="text-gray-600 dark:text-gray-400">Your feedback has been submitted. We appreciate it!</p>
                    <button
                      onClick={() => {
                        if (onClose) onClose();
                      }}
                      className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                )}
                {!feedbackSubmitted && (
                  <div className="mt-6 text-center">
                    <button
                      type="button"
                      className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 text-sm"
                      onClick={handleSkipFuture}
                    >
                      Skip Feedback for Now
                      <svg className="w-4 h-4 inline-block ml-1 align-middle" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}
              </motion.div>
            )}
          </div>
          {/* Footer with Refresh Button */}
          {activeTab === 'results' && (
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 flex justify-end flex-shrink-0">
              <button
                onClick={handleRefreshPage}
                className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
              >
                Play Again
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default GameOverModal;